import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSavedListings } from '../context/SavedListingsContext';
import { listings } from '../api/client';
import { config } from '../config';
import ListingCard from '../components/ListingCard';
import type { Listing } from '../api/client';

/** Coerce SavedListing to Listing for ListingCard (only display fields are needed). */
function toListing(
  s: { listingId: string; titleEn: string; titleAr?: string | null; price: number; currency: string; location: { city?: string; area?: string; country: string }; type: 'sale' | 'rent' | 'short_term'; mediaKeys?: string[]; bedrooms?: number; bathrooms?: number; areaSqm?: number },
  thumbnailUrl?: string | null
): Listing {
  return {
    ...s,
    orgId: 'saved',
    category: 'residential',
    status: 'published',
    descriptionEn: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    thumbnailUrl: thumbnailUrl ?? undefined,
    mediaUrls: thumbnailUrl ? [thumbnailUrl] : undefined,
  } as Listing;
}

const PAGE_SIZE = 12;

export default function Saved() {
  const { t } = useTranslation();
  const { saved, remove } = useSavedListings();
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(saved.length / PAGE_SIZE));
  const paginatedSaved = useMemo(
    () => saved.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [saved, currentPage]
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(Math.max(1, totalPages));
  }, [currentPage, totalPages]);

  const savedIds = useMemo(() => saved.map((s) => s.listingId).sort().join(','), [saved]);
  useEffect(() => {
    if (saved.length === 0 || !config.apiBaseUrl) return;
    let cancelled = false;
    const next: Record<string, string> = {};
    Promise.all(
      saved.map(async (item) => {
        if (cancelled || !item.listingId) return;
        try {
          const { urls } = await listings.getMediaUrls(item.listingId);
          if (!cancelled && urls && urls[0]) next[item.listingId] = urls[0];
        } catch {
          /* ignore */
        }
      })
    ).then(() => {
      if (!cancelled) setImageUrls((prev) => ({ ...prev, ...next }));
    });
    return () => { cancelled = true; };
  }, [savedIds, saved.length]);

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem', color: '#111' }}>
        {t('saved.title')}
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
        {saved.length === 0
          ? t('saved.empty')
          : t('saved.countSaved', { count: saved.length })}
      </p>

      {saved.length === 0 ? (
        <div
          style={{
            padding: '3rem 2rem',
            textAlign: 'center',
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #e5e7eb',
          }}
        >
          <Link to="/listings" style={{ color: '#0d9488', fontWeight: 600 }}>
            {t('saved.browseListings')}
          </Link>
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1.5rem',
            }}
          >
            {paginatedSaved.map((item) => (
              <div key={item.listingId} style={{ position: 'relative' }}>
                <ListingCard listing={toListing(item, imageUrls[item.listingId])} />
                <button
                  type="button"
                  onClick={() => remove(item.listingId)}
                  aria-label={t('saved.remove')}
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.95)',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#dc2626',
                    zIndex: 1,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          {saved.length > PAGE_SIZE && (
            <nav role="navigation" aria-label="Saved listings pagination" className="home-pagination" style={{ marginTop: '1.5rem' }}>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="home-pagination-btn home-pagination-btn--prev"
              >
                ← {t('common.previousPage')}
              </button>
              <span className="home-pagination-info">{t('common.pageN', { n: currentPage })} / {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="home-pagination-btn home-pagination-btn--next"
              >
                {t('common.nextPage')} →
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
